import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

/* The languages offered, in this order (the list the client chose, after
   vaidam.com's picker), each in its own script, with its country's code shown
   beside it in the list. Codes are Google Translate's. */
const LANGUAGES = [
  { code: "en", country: "GB", label: "English" },
  { code: "de", country: "DE", label: "Deutsch" },
  { code: "fr", country: "FR", label: "Français" },
  { code: "ru", country: "RU", label: "Русский" },
  { code: "nl", country: "NL", label: "Nederlands" },
  { code: "sv", country: "SE", label: "Svenska" },
  { code: "ar", country: "SA", label: "العربية" },
  { code: "it", country: "IT", label: "Italiano" },
  { code: "es", country: "ES", label: "Español" },
  { code: "bn", country: "BD", label: "বাংলা" },
  { code: "pt", country: "BR", label: "Português" },
];

/** The translate icon vaidam.com's picker uses (Google's Material Symbols
    "g_translate", one colour, drawn in the text colour) */
function TranslateIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 -960 960 960" fill="currentColor" className={className} aria-hidden="true" focusable="false">
      <path d="m480-80-40-120H160q-33 0-56.5-23.5T80-280v-520q0-33 23.5-56.5T160-880h240l35 120h365q35 0 57.5 22.5T880-680v520q0 33-22.5 56.5T800-80H480ZM286-376q69 0 113.5-44.5T444-536q0-8-.5-14.5T441-564H283v62h89q-8 28-30.5 43.5T287-443q-39 0-67-28t-28-69q0-41 28-69t67-28q18 0 34 6.5t29 19.5l49-47q-21-22-50.5-34T286-704q-67 0-114.5 47.5T124-540q0 69 47.5 116.5T286-376Zm268 20 22-21q-14-17-25.5-33T528-444l26 88Zm50-51q28-33 42.5-63t19.5-47H507l12 42h40q8 15 19 32.5t26 35.5Zm-84 287h280q18 0 29-11.5t11-28.5v-520q0-18-11-29t-29-11H447l47 162h79v-42h41v42h146v41h-51q-10 38-30 74t-47 67l109 107-29 29-108-108-36 37 32 111-80 80Z" />
    </svg>
  );
}

/** the language Google Translate's cookie asks for ("/en/fr" → "fr"), English if none */
function currentLanguage(): string {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/;]+\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "en";
}

function setTranslateCookie(code: string | null) {
  // Google's script sets the cookie on the site's host and also on the parent
  // domain (.rtechailabs.com for myvaidyam.rtechailabs.com). A copy left on
  // the parent kept the page translated after choosing English, so every
  // level is cleared, bare and dotted, before the new choice is written.
  const parts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) {
    const d = parts.slice(i).join(".");
    domains.push(`; domain=${d}`, `; domain=.${d}`);
  }
  for (const domain of domains) {
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
  }
  if (code) document.cookie = `googtrans=/en/${code}; path=/`;
}

/* Google Translate rewrites text in place, which React does not expect: when a
   translated node is later removed or moved, React can throw. These guards,
   the usual fix, let such calls pass quietly. Installed only while a page is
   being translated. */
function guardDomForTranslation() {
  const proto = Node.prototype as Node & { __mvGuarded?: boolean };
  if (proto.__mvGuarded) return;
  proto.__mvGuarded = true;
  const removeChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child) as T;
  };
  const insertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) return node;
    return insertBefore.call(this, node, ref) as T;
  };
}

/** Loads Google's translate widget, hidden; it reads the cookie and translates the page. */
function loadTranslator() {
  if (document.getElementById("google-translate-script")) return;
  guardDomForTranslation();
  const holder = document.createElement("div");
  holder.id = "google_translate_element";
  holder.style.display = "none";
  document.body.appendChild(holder);
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage: "en", autoDisplay: false }, "google_translate_element");
  };
  const s = document.createElement("script");
  s.id = "google-translate-script";
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

/**
 * The language picker. Choosing a language sets Google Translate's cookie and
 * reloads; the page then comes back translated. Google's script is fetched
 * only while a language other than English is chosen, so a visitor who keeps
 * English loads nothing from Google.
 */
export default function LanguageSelect() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const code = currentLanguage();
    setLang(code);
    if (code !== "en") loadTranslator();
  }, []);

  useEffect(() => {
    const away = (ev: MouseEvent) => {
      if (box.current && !box.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const choose = (code: string) => {
    setOpen(false);
    if (code === lang) return;
    setTranslateCookie(code === "en" ? null : code);
    window.location.reload();
  };

  const chosen = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  return (
    <div ref={box} className="notranslate relative shrink-0" translate="no">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${chosen.label}: choose a language`}
        className="flex h-10 items-center gap-2 rounded-md border border-[#2C4E5A]/20 bg-white px-3 text-sm font-semibold text-[#2C4E5A] shadow-sm transition-colors hover:border-[#2C4E5A]/40 sm:px-4"
      >
        <TranslateIcon className="h-5 w-5 shrink-0" />
        {/* on a phone the icon alone; the language from sm up */}
        <span className="hidden sm:inline">{chosen.label}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Language"
          className="absolute right-0 top-full z-[65] mt-2 max-h-[70vh] w-44 overflow-y-auto rounded-xl border border-border/60 bg-white p-1 shadow-xl"
        >
          {LANGUAGES.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                onClick={() => choose(l.code)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-[#EDE8D0]/70 ${
                  l.code === lang ? "font-semibold text-[#2C4E5A]" : "text-foreground"
                }`}
              >
                <span className="w-6 shrink-0 text-[11px] font-semibold tracking-wide text-foreground">{l.country}</span>
                <span className="flex-1">{l.label}</span>
                {l.code === lang && <Check className="h-4 w-4 shrink-0 text-[#2C4E5A]" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
